import React, { useState, useEffect } from 'react';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import { swalSuccess, swalError, swalConfirm } from '../utils/alert';
import './CardList.css';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

function CreditCardList({ onEdit }) {
  const [creditCards, setCreditCards] = useState([]);
  const [banks, setBanks] = useState([]);
  const width = useWindowWidth();

  const fetchBanks = () => {
    api.get('/banks')
      .then(response => setBanks(response.data))
      .catch(error => console.error('Houve um erro ao buscar os bancos!', error));
  };

  const fetchCreditCards = () => {
    api.get('/credit-cards')
      .then(response => setCreditCards(response.data))
      .catch(error => console.error('Houve um erro ao buscar os cartões de crédito!', error));
  };

  useEffect(() => {
    fetchBanks();
    fetchCreditCards();
  }, []);

  const getBankName = (bankAccountId) => {
    const bank = banks.find(b => b.id === bankAccountId);
    return bank ? bank.name : 'N/D';
  };

  const handleDelete = async (id) => {
    const result = await swalConfirm({ text: 'Tem certeza que deseja excluir este cartão de crédito?' });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/credit-cards/${id}`);
      swalSuccess('Cartão de crédito excluído com sucesso!');
      fetchCreditCards();
    } catch (error) {
      console.error('Houve um erro ao excluir o cartão de crédito!', error);
      swalError('Erro ao excluir cartão de crédito.');
    }
  };

  const usagePercent = (card) => {
    if (!card.limit || card.limit === 0) return 0;
    return Math.round(((card.limit - card.available_limit) / card.limit) * 100);
  };

  const renderMobileView = () => (
    <div>
      {creditCards.map(card => (
        <div key={card.id} className="card-list-card" style={{ borderLeft: `4px solid ${card.color || '#6c757d'}` }}>
          <div className="card-list-card-header">
            <h3>{card.name}</h3>
            <span className="badge bg-secondary">{card.brand}</span>
          </div>
          <div className="card-list-card-body">
            <p><strong>Final:</strong> •••• {card.last_four_digits}</p>
            <p><strong>Limite:</strong> {formatCurrency(card.limit)}</p>
            <p><strong>Disponível:</strong> {formatCurrency(card.available_limit)}</p>
            <div className="progress mb-2" style={{ height: '6px' }}>
              <div
                className={`progress-bar ${usagePercent(card) > 80 ? 'bg-danger' : usagePercent(card) > 50 ? 'bg-warning' : 'bg-success'}`}
                style={{ width: `${usagePercent(card)}%` }}
              />
            </div>
            <p><strong>Fechamento:</strong> dia {card.closing_day} &nbsp;|&nbsp; <strong>Vencimento:</strong> dia {card.due_day}</p>
            {card.bank_id && <p><strong>Emissor:</strong> {getBankName(card.bank_id)}</p>}
            <p><strong>Status:</strong> {card.status}</p>
          </div>
          <div className="card-list-card-footer">
            <button className="btn btn-warning btn-sm me-2" onClick={() => onEdit(card)}>Editar</button>
            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(card.id)}>Excluir</button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderDesktopView = () => (
    <table className="table table-striped table-hover">
      <thead className="thead-dark">
        <tr>
          <th>Nome</th>
          <th>Banco Emissor</th>
          <th>Bandeira</th>
          <th>Final</th>
          <th>Limite</th>
          <th>Disponível</th>
          <th>Fechamento</th>
          <th>Vencimento</th>
          <th>Conta Associada</th>
          <th>Status</th>
          <th>Cor</th>
          <th>Ações</th>
        </tr>
      </thead>
      <tbody>
        {creditCards.map(card => (
          <tr key={card.id}>
            <td>{card.name}</td>
            <td>{getBankName(card.bank_id)}</td>
            <td>{card.brand}</td>
            <td>{card.last_four_digits}</td>
            <td>{formatCurrency(card.limit)}</td>
            <td>{formatCurrency(card.available_limit)}</td>
            <td>{card.closing_day}</td>
            <td>{card.due_day}</td>
            <td>{getBankName(card.bank_account_id)}</td>
            <td>{card.status}</td>
            <td><span style={{ display: 'inline-block', width: 16, height: 16, borderRadius: '50%', backgroundColor: card.color || '#ccc' }}></span></td>
            <td>
              <button className="btn btn-warning btn-sm me-2" onClick={() => onEdit(card)}>Editar</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(card.id)}>Excluir</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div>
      <h2>Cartões de Crédito</h2>
      {width < 768 ? renderMobileView() : renderDesktopView()}
    </div>
  );
}

export default CreditCardList;
